import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, NavLink } from 'react-router-dom';
import queryString from 'query-string';
import {
  Table,
  useQueryState,
  IconExpand,
  Filters,
  useFilters,
  ButtonSize,
  ButtonPrimary,
  usePrevious,
  useGetList,
  PermissionEnum,
  GeneralLedgerTransaction,
  SchemaFilterType,
  AccountsSelector,
  Account,
  SortDir,
  Response,
  ButtonPseudo,
  IconDownload,
  SelectItemProps,
  AccountingJob,
  AccountingJobProps,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import ROUTES from '../../routes';
import {
  columns,
  columnsLabels,
  filtersSchema,
  schema,
} from './variables/list';
import {
  clearGeneralLedgerListState,
  setSelectedTake,
} from './store/slices/list';
import { setNotificationVisible } from '../../../../../store/slice';
import {
  useLazyGetGeneralLedgerListQuery,
  useLazyGetGeneralLedgerListStatusesQuery,
  useGetTransactionsAccountsCategoriesQuery,
  useLazyGetTransactionsAccountsListQuery,
  useExportGLTransactionMutation,
  useLazyGetGLAccountingJobsQuery,
  useLazyGetGLAccountingJobsStatesQuery,
} from './store/services';
import formatGLList from './helpers/format-gl-list';
import useGetAccountsAutocompleteList from '../../store/dictionaries/helpers/use-accounts-autocomplete';

import cn from './styles.module.css';

let timeout: NodeJS.Timeout;
let jobsTimeout: NodeJS.Timeout;

const GeneralLedgerList = (): React.JSX.Element => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    root: { userId },
    generalLedger: {
      list: {
        transactions,
        pagination,
        accountCategories,
        selectedTake,
        accountingJobs,
      },
    },
    transactionsDictionaries: { currencies, activePermissions },
  } = useAppSelector((state) => state);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [includeRemoved, setIncludeRemoved] = useState(false);
  const [onlyRemoved, setOnlyRemoved] = useState(false);
  const [savedFilters, setSavedFilters] = useState<
    {
      id: string;
      name: string;
      value: Record<string, any>;
      default?: boolean;
      employeeId: string;
    }[]
  >([]);
  const [selectedSavedFilter, setSelectedSavedFilter] = useState('');
  const [currentAccountingJob, setCurrentAccountingJob] =
    useState<AccountingJobProps>();

  const {
    autocompleteList: debitAutocompleteList,
    onSearch: onSearchDebit,
    onClear,
  } = useGetAccountsAutocompleteList('', 'id');
  const {
    autocompleteList: creditAutocompleteList,
    onSearch: onSearchCredit,
    onClear: onCreditClear,
  } = useGetAccountsAutocompleteList('', 'id');
  const [getGeneralLedgerStatuses] = useLazyGetGeneralLedgerListStatusesQuery();
  const [
    getAccountingJobs,
    {
      isLoading: accountingJobsIsLoading,
      isFetching: accountingJobsIsFetching,
    },
  ] = useLazyGetGLAccountingJobsQuery();
  const [getAccountingJobsStates] = useLazyGetGLAccountingJobsStatesQuery();
  const { isLoading: accountsCategoriesLoading } =
    useGetTransactionsAccountsCategoriesQuery(
      'all=true&include=primaryAccounts',
    );

  const [
    getTransactionsAccountsList,
    {
      isLoading: accountsListLoading,
      isFetching: accountsListUpdateInProgress,
    },
  ] = useLazyGetTransactionsAccountsListQuery();

  const { search } = useLocation();
  const dispatch = useAppDispatch();

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(schema);

  const [
    exportTransaction,
    { isSuccess: exportIsSuccess, isLoading: exportIsLoading },
  ] = useExportGLTransactionMutation();

  const onClearJobsTimeout = async () => {
    await clearTimeout(jobsTimeout);
  };

  const onGetStates = async (query: string) => {
    await onClearJobsTimeout();

    jobsTimeout = setTimeout(async () => {
      await getAccountingJobsStates(query);
      await onGetStates(query);
    }, 20000);
  };

  const onExportClick = async () => {
    const exportFilters: Record<string, string[] | string | number> = {};

    if (filters && Object.keys(filters)?.length) {
      Object.keys(filters)?.forEach((item) => {
        if (item !== 'cursor') {
          if (item !== 'transferDate') {
            if (
              item === 'id' ||
              item === 'creditAccountId' ||
              item === 'parentTransactionId' ||
              item === 'debitAccountId' ||
              item === 'currencyId' ||
              item === 'status' ||
              item === 'accountId'
            ) {
              exportFilters[item] = filters[item].value;
            } else {
              const [filterValue] = filters[item].value;

              exportFilters[item] = filterValue;
            }
          } else if (item === 'transferDate') {
            const transferDate = filters[item].value;

            const [from, to] = transferDate;

            exportFilters.transferDateFrom = from;

            if (to) {
              exportFilters.transferDateTo = to;
            }
          }
        }
      });
    }

    await exportTransaction(exportFilters);
  };

  const prevFilters = usePrevious(filters);

  const [getTransactionsList, { isLoading, isFetching }] =
    useLazyGetGeneralLedgerListQuery();

  const getTransactionsAccountsListAction = useCallback(
    async (query: string, accountId?: string) => {
      if (query && query.length) {
        const response = await getTransactionsAccountsList(
          `?${query}&all=true&sortProp=fullNumber&sortDir=asc`,
        );

        if (response && response.data) {
          setAccounts(response.data.data.edges.map((item) => item.node));

          if (accountId && !selectedAccount) {
            const currentAccount = response.data.data.edges.find(
              (item) => item.node.id === accountId,
            );

            if (currentAccount) {
              setSelectedAccount(currentAccount.node.fullNumber);
            }
          }
        }
      }
    },
    [],
  );

  const onSearch = async (searchText: string) => {
    const results: Account[] = [];

    if (searchText) {
      const response = (await getTransactionsAccountsList(
        `?search=${searchText}&all=true&sortProp=fullNumber&sortDir=asc`,
      )) as { data: Response<Account> };

      if (response?.data?.data) {
        response.data.data.edges?.forEach((item) => {
          results.push(item.node);
        });
      }

      setAccounts(results);
    }
  };

  const { getListAction, setPage, onFilter, onSort, errorList } =
    useGetList<GeneralLedgerTransaction>({
      getList: getTransactionsList,
      onFilterChange,
      onSortChange,
      schema,
    });

  const onSearchAutocomplete = (name: string, searchAutocomplete: string) => {
    if (name.toLowerCase().indexOf('debit') >= 0) {
      onSearchDebit(searchAutocomplete);
    } else if (name.toLowerCase().indexOf('credit') >= 0) {
      onSearchCredit(searchAutocomplete);
    }
  };

  const getAccountFromSavedFilter = (id: string) => {
    getTransactionsAccountsListAction(`ids=${id}`, id);
  };

  const onSavedFilterClick = (id: string, savedFilter: Filters) => {
    const filterValues: Filters = {
      ...savedFilter,
      take: {
        value: [(selectedTake as number)?.toString()],
        type: SchemaFilterType.STRING,
      },
    };

    if (savedFilter?.accountId) {
      const currentAccount = accounts?.find(
        (item) => item.id === savedFilter.accountId?.value[0],
      );

      if (currentAccount) {
        setSelectedAccount(currentAccount.fullNumber);
      } else {
        getAccountFromSavedFilter(savedFilter.accountId?.value[0]);
      }
    }

    onFilter(filterValues);
    setSelectedSavedFilter(id);
  };

  const onClearTimeout = async () => {
    await clearTimeout(timeout);
  };

  const onGetStatuses = async (query: string) => {
    await onClearTimeout();

    timeout = setTimeout(async () => {
      await getGeneralLedgerStatuses(query);
      await onGetStatuses(query);
    }, 5000);
  };

  const onClearAutoComplete = () => {
    onClear();

    setTimeout(() => {
      onCreditClear();
    }, 100);
  };

  const { filtersComponent, setValues, handleSubmit, reset } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
    onSearchAutocomplete,
  });

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = offsetHeight / 45;

        dispatch(setSelectedTake(Math.ceil(take + 3)));
      }
    }
  }, [scrollRef]);

  useEffect(() => {
    if (
      accountingJobs &&
      accountingJobs?.length &&
      !accountingJobsIsFetching &&
      !accountingJobsIsLoading
    ) {
      let currentJob: AccountingJobProps | undefined;

      if (accountingJobs?.find((item) => item.state === 'IN_PROGRESS')) {
        currentJob = accountingJobs?.find(
          (item) => item.state === 'IN_PROGRESS',
        );
      } else if (
        !accountingJobs?.find((item) => item.state === 'IN_PROGRESS') &&
        accountingJobs?.find((item) => item.state === 'QUEUED')
      ) {
        currentJob = accountingJobs?.find((item) => item.state === 'QUEUED');
      } else if (
        !accountingJobs?.find((item) => item.state === 'IN_PROGRESS') &&
        !accountingJobs?.find((item) => item.state === 'QUEUED') &&
        accountingJobs?.find((item) => item.state === 'NEW')
      ) {
        currentJob = accountingJobs?.find((item) => item.state === 'NEW');
      }

      if (currentJob) {
        setCurrentAccountingJob(currentJob);
      }
    }
  }, [accountingJobs, accountingJobsIsFetching, accountingJobsIsLoading]);

  useEffect(() => {
    if (
      activePermissions?.includes(PermissionEnum.REPORTS_ACCOUNTING_JOBS_LITS)
    ) {
      getAccountingJobs(
        '?type=POST_PROCESSING&state=IN_PROGRESS&state=QUEUED&state=NEW&all=true',
      );

      onGetStates(
        '?type=POST_PROCESSING&state=IN_PROGRESS&state=QUEUED&state=NEW&all=true',
      );
    }
  }, [activePermissions]);

  useEffect(
    () => () => {
      setTimeout(async () => {
        await onClearTimeout();
      });
    },
    [],
  );

  useEffect(() => {
    if (transactions && transactions.length) {
      const ids = transactions.map((item) => item.id);
      const query = `?id=${ids.join('&id=')}`;

      onGetStatuses(query);
    }
  }, [transactions]);

  useEffect(() => {
    setValues('debitAccountId', debitAutocompleteList);
    setValues('creditAccountId', creditAutocompleteList);
    setValues('currencyId', currencies as SelectItemProps[]);
    setValues(
      'accountId',
      accounts?.map((item) => ({
        name: item.description,
        value: item.id,
        suffix: item.fullNumber,
      })),
    );
  }, [debitAutocompleteList, creditAutocompleteList, currencies, accounts]);

  useEffect(() => {
    if (filters?.accountId?.value?.length && !transactions) {
      getTransactionsAccountsListAction(
        `ids=${filters?.accountId?.value[0]}`,
        filters?.accountId?.value[0],
      );
    }
  }, [filters]);

  useEffect(() => {
    if (exportIsSuccess) {
      dispatch(
        setNotificationVisible({
          visible: true,
          text: "Report in progress. You'll be notified.",
          type: 'general-ledger',
        }),
      );
    }
  }, [exportIsSuccess]);

  useEffect(() => {
    if (
      selectedTake &&
      selectedTake !== 0 &&
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !transactions &&
      sort &&
      sort.sortDir
    ) {
      let parsed;

      if (search) {
        parsed = queryString.parse(search);
      }

      let filterValues: Filters = {
        ...filters,
        take: {
          value: [selectedTake?.toString()],
          type: SchemaFilterType.STRING,
        },
      };

      const savedFiltersJSON = localStorage.getItem('general-ledger-filters');

      let parsedSavedFilters: {
        id: string;
        name: string;
        value: Record<string, any>;
        default?: boolean;
      }[];

      let defaultSavedFilter;

      if (!Object.keys(filters)?.length) {
        if (savedFiltersJSON) {
          parsedSavedFilters = JSON.parse(savedFiltersJSON);

          if (parsedSavedFilters && parsedSavedFilters?.length) {
            const currentUserParsedFilters = parsedSavedFilters?.filter(
              (item: any) => item.employeeId === userId,
            );

            if (currentUserParsedFilters && currentUserParsedFilters?.length) {
              defaultSavedFilter = currentUserParsedFilters?.find(
                (item) => item.default,
              );
            }
          }
        }

        if (defaultSavedFilter) {
          setSelectedSavedFilter(defaultSavedFilter.id);

          if (defaultSavedFilter?.value?.accountId) {
            getAccountFromSavedFilter(
              defaultSavedFilter?.value?.accountId?.value[0],
            );
          }

          filterValues = { ...filterValues, ...defaultSavedFilter?.value };
        } else {
          filterValues = { ...filterValues, ...filters };
        }
      } else {
        filterValues = { ...filterValues, ...filters };
      }

      if (parsed && parsed.includeRemoved) {
        setIncludeRemoved(true);

        filterValues.includeRemoved = {
          value: ['true'],
          type: SchemaFilterType.STRING,
        };
      }

      if (parsed && parsed.onlyRemoved) {
        setOnlyRemoved(true);

        filterValues.onlyRemoved = {
          value: ['true'],
          type: SchemaFilterType.STRING,
        };
      }

      getListAction({ filters: filterValues, sort });
    }
  }, [filters, sort, transactions, selectedTake]);

  useEffect(
    () => () => {
      dispatch(clearGeneralLedgerListState());
      onClearTimeout();
    },
    [dispatch],
  );

  const formattedData = useMemo(() => {
    const data = formatGLList({
      list: transactions as GeneralLedgerTransaction[],
    });

    return data;
  }, [transactions]);

  useEffect(() => {
    const savedFiltersJSON = localStorage.getItem('general-ledger-filters');

    if (savedFiltersJSON) {
      const parsed = JSON.parse(savedFiltersJSON);

      if (parsed) {
        const currentEmployeeSavedFilters = parsed?.filter(
          (item: any) => item.employeeId === userId,
        );

        setSavedFilters(currentEmployeeSavedFilters);
      }
    }
  }, [userId]);

  const content: React.JSX.Element = (
    <>
      <div className={cn.glTransactionTop}>
        <div className={cn.glTransactionTopTitle}>
          General ledger transactions
        </div>
        <div className={cn.glTopContent}>
          <div className={cn.accountSelectorWrapper}>
            <AccountsSelector
              accounts={accountCategories}
              placeholder="Enter name or number..."
              isLoading={accountsCategoriesLoading}
              selectedAccount={selectedAccount}
              accountsIsLoading={
                accountsListLoading || accountsListUpdateInProgress
              }
              onSearch={onSearch}
              setAccountQuery={getTransactionsAccountsListAction}
              accountsList={accounts}
              setAccounts={setAccounts}
              setSelectedAccount={(number) => {
                setSelectedSavedFilter('');
                setSelectedAccount(number);

                const currentAccount = accounts?.find(
                  (item) => item.fullNumber === number,
                );

                let nextFilters: Filters = {
                  ...filters,
                  accountId: { value: [] },
                  take: {
                    value: [(selectedTake as number)?.toString()],
                    type: SchemaFilterType.STRING,
                  },
                  sortProp: {
                    value: sort.sortProp ? [sort.sortProp] : ['transferDate'],
                    type: SchemaFilterType.STRING,
                  },
                  sortDir: {
                    value: sort.sortDir ? [sort.sortDir] : [SortDir.DESC],
                    type: SchemaFilterType.STRING,
                  },
                };

                if (includeRemoved) {
                  nextFilters = {
                    ...nextFilters,
                    includeRemoved: {
                      value: ['true'],
                      type: SchemaFilterType.STRING,
                    },
                  };
                }

                if (onlyRemoved) {
                  nextFilters = {
                    ...nextFilters,
                    onlyRemoved: {
                      value: ['true'],
                      type: SchemaFilterType.STRING,
                    },
                  };
                }

                if (currentAccount) {
                  nextFilters = {
                    ...nextFilters,
                    accountId: {
                      value: [currentAccount.id],
                      type: SchemaFilterType.STRING,
                    },
                  };
                }

                reset(nextFilters);
                onFilterChange(nextFilters);

                getListAction({ filters: nextFilters, sort });
              }}
            />
          </div>
        </div>
        {activePermissions?.includes(
          PermissionEnum.REPORTS_ACCOUNTING_JOBS_LITS,
        ) && (
          <AccountingJob
            accountingJobsIsLoading={
              accountingJobsIsLoading || accountingJobsIsFetching
            }
            accountingJobs={accountingJobs}
            accountingJob={currentAccountingJob}
            jobsLength={accountingJobs?.length}
            query="?type=POST_PROCESSING&state=IN_PROGRESS&state=QUEUED&state=NEW"
          />
        )}
      </div>

      <Table
        columns={columns}
        columnsLabels={columnsLabels}
        data={formattedData || []}
        filters={filters}
        sort={sort}
        filtersComponent={filtersComponent}
        tableBodyClassName={cn.glTableBody}
        tableControlsClassName={cn.glTableControls}
        onSort={(name) => {
          onSort(name);
          onClearTimeout();
        }}
        onFilterChange={(values) => {
          setSelectedSavedFilter('');

          let nextFilters: Filters = {
            ...values,
            sortProp: {
              value: sort.sortProp ? [sort.sortProp] : ['transferDate'],
              type: SchemaFilterType.STRING,
            },
            sortDir: {
              value: sort.sortDir ? [sort.sortDir] : [SortDir.DESC],
              type: SchemaFilterType.STRING,
            },
            take: {
              value: [(selectedTake as number)?.toString()],
              type: SchemaFilterType.STRING,
            },
          };

          if (includeRemoved) {
            nextFilters = {
              ...nextFilters,
              includeRemoved: {
                value: ['true'],
                type: SchemaFilterType.STRING,
              },
            };
          }

          if (onlyRemoved) {
            nextFilters = {
              ...nextFilters,
              onlyRemoved: { value: ['true'], type: SchemaFilterType.STRING },
            };
          }

          if (selectedAccount && values.accountId) {
            const currentAccount = accounts?.find(
              (item) => item.fullNumber === selectedAccount,
            );

            if (currentAccount) {
              nextFilters = {
                ...nextFilters,
                accountId: {
                  value: [currentAccount.id],
                  type: SchemaFilterType.STRING,
                },
              };
            }
          }

          if (!values.accountId && selectedAccount && selectedSavedFilter) {
            setSelectedAccount('');
          }

          onFilter({ ...nextFilters });
          onClearTimeout();
        }}
        handleSubmit={handleSubmit}
        isLoading={
          isLoading || isFetching || !selectedTake || transactions === undefined
        }
        topContent={
          <>
            {activePermissions?.includes(
              PermissionEnum.TRANSACTIONS_GENERAL_LEDGER_CREATE_ACTION,
            ) ? (
              <NavLink to={`${ROUTES.GENERAL_LEDGER_CREATE}${search || ''}`}>
                <ButtonPrimary
                  text="New GL transaction"
                  size={ButtonSize.SMALL}
                  icon={<IconExpand />}
                />
              </NavLink>
            ) : undefined}
            <ButtonPseudo
              text={exportIsLoading ? 'Loading...' : 'Export transactions'}
              icon={<IconDownload />}
              className={`button-white ${cn.exportButton}`}
              size={ButtonSize.SMALL}
              onClick={onExportClick}
            />
          </>
        }
        take={(selectedTake as number)?.toString()}
        setPage={(data) => {
          const filterValues: Filters = {
            ...filters,
            take: {
              value: [(selectedTake as number)?.toString()],
              type: SchemaFilterType.STRING,
            },
          };

          if (includeRemoved) {
            filterValues.includeRemoved = {
              value: ['true'],
              type: SchemaFilterType.STRING,
            };
          }

          if (onlyRemoved) {
            filterValues.onlyRemoved = {
              value: ['true'],
              type: SchemaFilterType.STRING,
            };
          }

          setPage({ ...data, filters: filterValues });
          onClearTimeout();
        }}
        onClearAutoComplete={onClearAutoComplete}
        pagination={pagination}
        storeKey="gl-transaction"
        tableTopClassName={cn.glTableTop}
        tableClassName={!transactions?.length ? cn.glTable : ''}
        hiddenFiltersTags={['accountId']}
        paginationDisabled={isFetching}
        error={errorList}
        isFetching={isFetching}
        savedFilters={savedFilters}
        setSavedFilters={setSavedFilters}
        savedFiltersKey="general-ledger-filters"
        selectedSavedFilter={selectedSavedFilter}
        onSavedFilterClick={onSavedFilterClick}
        setSelectedSavedFilter={setSelectedSavedFilter}
        userId={userId}
        transactionTable
        withVerticalScroll
        withSettings
        withScroll
        clickable
      />
    </>
  );

  return (
    <div className={cn.generalLedgerWrapper} ref={scrollRef}>
      {content}
      <div id="delete-popup" />
    </div>
  );
};

export default GeneralLedgerList;
